import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/App";
import { Mic2, Swords, Trophy, Flame, Users, Vote, Zap, ChevronRight, Play, Crown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import MatchupCard from "@/components/MatchupCard";

// Hero Spardom Logo - Brand presence on landing (slightly smaller than sign-in)
const HeroSpardomLogo = ({ className = "" }) => (
  <div className={`flex flex-col items-center justify-center ${className}`}>
    <img 
      src="/spardom-logo.png" 
      alt="Spardom" 
      className="h-40 sm:h-44 md:h-48 lg:h-60 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
    />
  </div>
);

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [debates, setDebates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [arenaMatchup, setArenaMatchup] = useState(null);
  const [arenaStats, setArenaStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [debatesRes, categoriesRes, arenaRes] = await Promise.all([
          axios.get(`${API}/debates?limit=6`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/arena/current`).catch(() => ({ data: { matchup: null } }))
        ]);
        setDebates(debatesRes.data);
        setCategories(categoriesRes.data);
        setArenaMatchup(arenaRes.data.matchup);
        setArenaStats(arenaRes.data.stats);
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      {/* Ambient ember glow - neutral, non-political */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-[#FF7A18]/15 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-[#FF9A4A]/10 blur-[100px]" />
      </div>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-white/10">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1765224747205-3c9c23f0553c?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          {/* Hero Logo - Prominent Branding */}
          <div className="mb-6">
            <HeroSpardomLogo className="animate-fade-in" />
          </div>
          
          {/* Tagline / Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase mb-4 leading-tight">
            Spar For <span className="text-[#FF7A18]">Stardom</span>
          </h1>
          
          {/* Hero subtitle - locked copy */}
          <p className="text-base md:text-lg lg:text-xl font-bold text-white tracking-wide whitespace-nowrap max-w-full mx-auto mb-8">
            Video debates. Stack wins. Grow a fanbase.
          </p>
          
          {/* THE ARENA Card - Neutral dark theme with ember accent */}
          <div 
            onClick={() => navigate("/arena")}
            className="mb-8 max-w-lg mx-auto cursor-pointer group"
          >
            <div className="bg-gradient-to-r from-[#182331] via-zinc-900/90 to-[#182331] border-2 border-[#FF7A18]/60 rounded-xl p-5 hover:border-[#FF7A18] transition-all hover:scale-[1.02] shadow-xl shadow-[#FF7A18]/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Flame className="w-6 h-6 text-[#FF7A18]" />
                  <span className="font-black text-base uppercase tracking-wider text-[#FF7A18]">THE ARENA</span>
                </div>
                <Badge variant="outline" className="text-[#FF7A18] border-[#FF7A18]/40 text-xs font-bold">
                  Daily Spar
                </Badge>
              </div>
              
              {arenaMatchup ? (
                <>
                  {/* TODAY'S MATCHUP - Neutral colors with Side labels */}
                  <div className="bg-black/40 rounded-lg p-4 mb-3">
                    <p className="text-xs text-[#FF7A18]/80 uppercase tracking-widest font-bold mb-3 text-center">Today&apos;s Matchup</p>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 text-center">
                        <span className="text-[10px] uppercase tracking-wider text-[#8A96A6] font-bold block mb-1">SIDE A</span>
                        <span className="font-black text-white text-lg md:text-xl leading-tight drop-shadow-lg">{arenaMatchup.side_a}</span>
                      </div>
                      <div className="shrink-0 px-2">
                        <span className="text-2xl font-black text-[#FF7A18]">VS</span>
                      </div>
                      <div className="flex-1 text-center">
                        <span className="text-[10px] uppercase tracking-wider text-[#8A96A6] font-bold block mb-1">SIDE B</span>
                        <span className="font-black text-white text-lg md:text-xl leading-tight drop-shadow-lg">{arenaMatchup.side_b}</span>
                      </div>
                    </div>
                  </div>
                  
                  {arenaStats && arenaStats.total_votes > 0 && (
                    <div className="mt-3">
                      <div className="h-2 rounded-full overflow-hidden bg-zinc-800 flex">
                        <div 
                          className="transition-all"
                          style={{ width: `${arenaStats.heat_a}%`, backgroundColor: '#2B2F36' }}
                        />
                        <div 
                          className="transition-all"
                          style={{ width: `${arenaStats.heat_b}%`, backgroundColor: '#FF7A18' }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span className="font-bold text-[#B7C3D0]">A: {arenaStats.heat_a}%</span>
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3 text-[#FF7A18]" />
                          {arenaStats.total_votes} votes
                        </span>
                        <span className="font-bold text-[#FF7A18]">B: {arenaStats.heat_b}%</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Empty state when no matchup */
                <div className="bg-black/40 rounded-lg p-4 mb-3 text-center">
                  <Swords className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">New matchup coming soon!</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Check back for today&apos;s debate</p>
                </div>
              )}
              
              <div className="mt-4 text-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF7A18]/20 hover:bg-[#FF7A18]/30 border border-[#FF7A18]/50 rounded-full text-sm font-bold text-[#FF9A4A] group-hover:text-white transition-all">
                  Enter The Arena
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </div>
            </div>
          </div>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              data-testid="watch-debates-btn"
              onClick={() => navigate("/browse")}
              variant="outline"
              className="border-2 border-white/20 bg-transparent hover:bg-white/10 text-foreground h-14 px-10 rounded-md font-bold uppercase tracking-wider"
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Debates
            </Button>
            <Button 
              data-testid="enter-arena-btn"
              onClick={() => navigate(user ? "/browse" : "/register")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 rounded-md font-bold uppercase tracking-wider btn-electric text-lg"
            >
              <Swords className="w-5 h-5 mr-2" />
              Become a Debater
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-center mb-16">
            How It <span className="text-primary">Works</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card/50 backdrop-blur-sm p-8 border border-white/5 hover:border-primary/50 transition-all duration-300 rounded-lg group">
              <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Mic2 className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">🔥 Hot Take</h3>
              <p className="text-muted-foreground">
                Make your case on camera.
              </p>
            </div>
            
            <div className="bg-card/50 backdrop-blur-sm p-8 border border-white/5 hover:border-secondary/50 transition-all duration-300 rounded-lg group">
              <div className="w-14 h-14 rounded-lg bg-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Swords className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="text-xl font-bold mb-3">👏 Clapback</h3>
              <p className="text-muted-foreground">
                Respond. Counter. Control the room.
              </p>
            </div>
            
            <div className="bg-card/50 backdrop-blur-sm p-8 border border-white/5 hover:border-accent/50 transition-all duration-300 rounded-lg group">
              <div className="w-14 h-14 rounded-lg bg-accent/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Trophy className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-3">🎤 Mic Drop</h3>
              <p className="text-muted-foreground">
                Finish strong. Let the crowd decide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Spardom Exists - Mic Drop Statement */}
      <section className="py-24 md:py-32 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-lg md:text-xl lg:text-2xl font-bold text-white tracking-wide uppercase">
            Comment sections don&apos;t reward skill.
          </p>
          <p className="text-4xl md:text-5xl lg:text-6xl font-black text-[#FF7A18] tracking-tight uppercase drop-shadow-[0_0_30px_rgba(255,122,24,0.3)]">
            Spardom does 🔥
          </p>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              Pick Your <span className="text-secondary">Arena</span>
            </h2>
            <Link to="/browse" className="text-muted-foreground hover:text-foreground flex items-center gap-2 group">
              View All <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.slice(0, 6).map((category) => (
              <Link 
                key={category.category_id}
                to={`/browse?category=${category.slug}`}
                data-testid={`category-${category.slug}`}
                className="group relative h-48 rounded-lg overflow-hidden border border-white/10 hover:border-primary/50 transition-all"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${category.image_url}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold mb-1">{category.name}</h3>
                  <p className="text-sm text-muted-foreground">{category.debate_count} debates</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Debates */}
      {debates.length > 0 && (
        <section className="py-24 px-6 border-t border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                <Flame className="inline w-10 h-10 text-primary mr-3" />
                Trending <span className="text-primary">Battles</span>
              </h2>
              <Link to="/browse" className="text-muted-foreground hover:text-foreground flex items-center gap-2 group">
                View All <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {debates.slice(0, 6).map((debate) => (
                <MatchupCard key={debate.debate_id} debate={debate} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <Crown className="w-16 h-16 text-accent mx-auto mb-6 animate-float" />
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to Prove Yourself?
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Spardom is invite-only during early access. 
            Got a code? Join the elite debaters making their mark.
          </p>
          <Button 
            data-testid="join-now-btn"
            onClick={() => navigate(user ? "/create" : "/register")}
            className="bg-accent text-accent-foreground hover:bg-accent/90 h-14 px-12 rounded-md font-bold uppercase tracking-wider btn-electric text-lg"
          >
            <Zap className="w-5 h-5 mr-2" />
            {user ? "Start Debating" : "Enter with Invite Code"}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Swords className="w-6 h-6 text-primary" />
            <span className="font-bold text-lg">Spardom</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 Spardom. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
